import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Landmark, Building2, User, Calculator, Users2, KeyRound, ClipboardList } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ACCOUNT_TYPES = {
  CHECKING: 'Conta corrente',
  SAVINGS: 'Poupança',
  DIGITAL: 'Conta digital',
  OTHER: 'Outra',
};

export default function Settings() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader title="Configurações" subtitle="Gerencie sua empresa, perfil e contas bancárias." />

      <Tabs defaultValue="empresa">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl h-auto">
          <TabsTrigger value="empresa" data-testid="settings-tab-company" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Building2 className="h-4 w-4 shrink-0" /> <span>Empresa</span>
          </TabsTrigger>
          <TabsTrigger value="perfil" data-testid="settings-tab-profile" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <User className="h-4 w-4 shrink-0" /> <span>Perfil</span>
          </TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="settings-tab-users" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Users2 className="h-4 w-4 shrink-0" /> <span>Usuários</span>
          </TabsTrigger>
          <TabsTrigger value="contas" data-testid="settings-tab-accounts" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Landmark className="h-4 w-4 shrink-0" /> <span>Contas</span>
          </TabsTrigger>
          <TabsTrigger value="checklist" data-testid="settings-tab-checklist" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <ClipboardList className="h-4 w-4 shrink-0" /> <span>Checklist</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" className="mt-4 space-y-4">
          <Card className="border-border/70 max-w-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Empresa</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Nome da empresa</Label>
                <Input value={tenant?.name || ''} disabled data-testid="settings-company-name" />
              </div>
              <div>
                <Label>ID</Label>
                <Input value={tenant?.id || ''} disabled className="font-mono text-xs" />
              </div>
              <p className="text-xs text-muted-foreground">Edição de dados da empresa estará disponível em breve.</p>
            </CardContent>
          </Card>

          <TaxSettingsPanel />
        </TabsContent>

        <TabsContent value="perfil" className="mt-4">
          <Card className="border-border/70 max-w-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Perfil</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={user?.name || ''} disabled data-testid="settings-user-name" />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input value={user?.email || ''} disabled />
              </div>
              <div>
                <Label>Perfil</Label>
                <Input value={user?.role || 'admin'} disabled />
              </div>
              <Separator />
              <Button variant="outline" onClick={() => { logout(); navigate('/login'); }} data-testid="settings-logout">
                Sair da conta
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contas" className="mt-4">
          <BankAccountsPanel />
        </TabsContent>

        <TabsContent value="usuarios" className="mt-4">
          <UsersPanel />
        </TabsContent>

        <TabsContent value="checklist" className="mt-4">
          <ChecklistTemplatesPanel />
        </TabsContent>
      </Tabs>
    </>
  );
}

function BankAccountsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', bank: '', agency: '', account_number: '', account_type: '', holder: '', notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/accounts');
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', bank: '', agency: '', account_number: '', account_type: '', holder: '', notes: '' });
    setShow(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '',
      bank: a.bank || '',
      agency: a.agency || '',
      account_number: a.account_number || '',
      account_type: a.account_type || '',
      holder: a.holder || '',
      notes: a.notes || '',
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Nome / apelido é obrigatório'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        bank: form.bank.trim() || null,
        agency: form.agency.trim() || null,
        account_number: form.account_number.trim() || null,
        account_type: form.account_type || null,
        holder: form.holder.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await api.put(`/accounts/${editing.id}`, payload);
      } else {
        await api.post('/accounts', payload);
      }
      toast.success(editing ? 'Conta atualizada' : 'Conta cadastrada');
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Excluir conta "${a.name}"?`)) return;
    try {
      await api.delete(`/accounts/${a.id}`);
      toast.success('Conta excluída');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível excluir');
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Contas bancárias da empresa para registrar a origem dos pagamentos.</p>
        <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="account-new-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nova conta
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={Landmark}
          title="Nenhuma conta bancária cadastrada"
          description="Cadastre contas para registrar a origem dos pagamentos de compra dos veículos."
          action={{ label: 'Cadastrar conta', onClick: openCreate }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <Card key={a.id} className="border-border/70" data-testid="account-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{a.name}</div>
                    {a.bank && <div className="text-xs text-muted-foreground truncate">{a.bank}</div>}
                    {(a.agency || a.account_number) && (
                      <div className="text-xs text-muted-foreground tabular">
                        {a.agency && `Ag. ${a.agency}`}{a.agency && a.account_number && ' · '}{a.account_number && `Cc. ${a.account_number}`}
                      </div>
                    )}
                    {a.account_type && <div className="text-xs text-muted-foreground mt-1">{ACCOUNT_TYPES[a.account_type] || a.account_type}</div>}
                    {a.holder && <div className="text-xs text-muted-foreground">Titular: {a.holder}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid="account-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(a)} data-testid="account-delete">
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar conta' : 'Nova conta bancária'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Apelido / Nome da conta *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Itaú PJ Principal" data-testid="account-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input value={form.bank} onChange={(e) => setForm((f) => ({ ...f, bank: e.target.value }))} placeholder="Itaú, Nubank..." />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm((f) => ({ ...f, account_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACCOUNT_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agência</Label>
                <Input value={form.agency} onChange={(e) => setForm((f) => ({ ...f, agency: e.target.value }))} />
              </div>
              <div>
                <Label>Conta</Label>
                <Input value={form.account_number} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Titular</Label>
              <Input value={form.holder} onChange={(e) => setForm((f) => ({ ...f, holder: e.target.value }))} placeholder="Nome do titular ou razão social" />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="account-save-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TaxSettingsPanel() {
  const { tenant } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [icmsState, setIcmsState] = useState('SP');
  const [icmsRate, setIcmsRate] = useState(18);
  const [icmsReduction, setIcmsReduction] = useState(90);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      const t = data.tenant || {};
      setEnabled(Boolean(t.tax_calculation_enabled));
      setIcmsState(t.icms_state || 'SP');
      setIcmsRate((t.icms_rate ?? 0.18) * 100);
      setIcmsReduction((t.icms_base_reduction_pct ?? 0.90) * 100);
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/settings', {
        tax_calculation_enabled: enabled,
        icms_state: icmsState,
        icms_rate: Number(icmsRate) / 100,
        icms_base_reduction_pct: Number(icmsReduction) / 100,
      });
      toast.success('Configurações fiscais atualizadas');
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-48 max-w-2xl rounded-xl" />;

  return (
    <Card className="border-border/70 max-w-2xl" data-testid="tax-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Calculator className="h-4 w-4 text-muted-foreground" /> Cálculo de impostos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-start gap-3 p-3 rounded-lg border border-border/70 hover:bg-muted/30 cursor-pointer" data-testid="tax-enable-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
          />
          <div className="flex-1">
            <div className="text-sm font-medium">Ativar cálculo automático de impostos</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Quando ativado, todo veículo vendido terá o cálculo de PIS, COFINS, IRPJ, CSLL e ICMS aplicado automaticamente sobre a DRE. Regime: <strong>Lucro Presumido</strong>.
            </div>
          </div>
        </label>

        {enabled && (
          <div className="space-y-3 rounded-lg bg-muted/30 border border-border/70 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Configuração ICMS</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Estado</Label>
                <Select value={icmsState} onValueChange={setIcmsState}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['SP','MG','RJ','PR','RS','SC','BA','GO','PE','CE','DF','MT','MS','ES','PA','AM','RN','PB','AL','SE','PI','MA','TO','RO','AC','AP','RR'].map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Alíquota ICMS (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={icmsRate}
                  onChange={(e) => setIcmsRate(e.target.value)}
                  className="text-right tabular"
                />
              </div>
              <div>
                <Label>Redução base (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={icmsReduction}
                  onChange={(e) => setIcmsReduction(e.target.value)}
                  className="text-right tabular"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Em SP, veículos usados têm <strong>90% de redução de base de cálculo</strong>. Alíquota efetiva resultante: <strong>{(Number(icmsRate)/100 * (1 - Number(icmsReduction)/100) * 100).toFixed(2)}%</strong> sobre o valor da venda.
            </p>
          </div>
        )}

        {enabled && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs space-y-1.5">
            <div className="font-semibold text-blue-900">Alíquotas aplicadas (sobre margem fiscal):</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-blue-900">
              <div>PIS: <span className="tabular font-semibold">0,65%</span></div>
              <div>COFINS: <span className="tabular font-semibold">3,00%</span></div>
              <div>IRPJ: <span className="tabular font-semibold">4,80%</span> (32% × 15%)</div>
              <div>CSLL: <span className="tabular font-semibold">2,88%</span> (32% × 9%)</div>
            </div>
            <div className="text-blue-800 mt-1.5">
              ℹ️ <strong>Adicional IRPJ trimestral (10%)</strong>: incide sobre o que exceder R$ 60.000 de lucro presumido no trimestre. Calculado no Dashboard de forma consolidada.
            </div>
          </div>
        )}

        <Separator />
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="tax-save-button">
            {saving ? 'Salvando...' : 'Salvar configurações'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UsersPanel() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', role: 'admin' });
    setShow(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || '', email: u.email || '', password: '', role: u.role || 'admin' });
    setShow(true);
  };

  const submit = async () => {
    if (!editing) {
      if (!form.name.trim() || !form.email.trim() || !form.password) {
        toast.error('Nome, e-mail e senha são obrigatórios');
        return;
      }
      if (form.password.length < 6) {
        toast.error('Senha deve ter no mínimo 6 caracteres');
        return;
      }
    }
    try {
      if (editing) {
        const payload = { name: form.name.trim(), role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success('Usuário atualizado');
      } else {
        await api.post('/users', {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
        });
        toast.success('Usuário cadastrado');
      }
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir usuário ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Usuário excluído');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível excluir');
    }
  };

  if (loading) {
    return <Skeleton className="h-48 max-w-2xl rounded-xl" />;
  }

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 max-w-3xl">
        <p className="text-sm text-muted-foreground">Usuários com acesso ao tenant da sua empresa.</p>
        <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 self-start" data-testid="user-new-button">
          <Plus className="h-4 w-4 mr-1.5" /> Novo usuário
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        {items.map((u) => {
          const isSelf = u.id === currentUser?.id;
          return (
            <Card key={u.id} className="border-border/70" data-testid="user-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted grid place-items-center text-muted-foreground shrink-0">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold truncate">{u.name}</div>
                      {isSelf && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]">você</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{u.role || 'admin'}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Editar" data-testid="user-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {!isSelf && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} title="Excluir" data-testid="user-delete">
                        <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" />
            </div>
            <div>
              <Label>E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={!!editing} data-testid="user-email-input" />
              {editing && <p className="text-[11px] text-muted-foreground mt-1">E-mail não pode ser alterado após o cadastro.</p>}
            </div>
            <div>
              <Label>{editing ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={editing ? 'Não alterar' : 'Mínimo 6 caracteres'}
                  data-testid="user-password-input"
                />
                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground">
                  <KeyRound className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div>
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Por enquanto, apenas perfil "Administrador" disponível.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="user-save-button">
              {editing ? 'Salvar alterações' : 'Cadastrar usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============== CHECKLIST TEMPLATES PANEL ==============
function ChecklistTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', order: 0, default_due_days: '', is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/checklist-templates');
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', order: 0, default_due_days: '', is_active: true });
    setShow(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      order: t.order ?? 0,
      default_due_days: t.default_due_days ?? '',
      is_active: t.is_active ?? true,
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        order: Number(form.order) || 0,
        default_due_days: form.default_due_days ? Number(form.default_due_days) : null,
        is_active: form.is_active,
      };
      if (editing) {
        await api.put(`/checklist-templates/${editing.id}`, payload);
        toast.success('Template atualizado');
      } else {
        await api.post('/checklist-templates', payload);
        toast.success('Template criado');
      }
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir template "${t.name}"?`)) return;
    try {
      await api.delete(`/checklist-templates/${t.id}`);
      toast.success('Template excluído');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível excluir');
    }
  };

  if (loading) {
    return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Templates de checklist padrão para todos os veículos. Ao adicionar um veículo, você pode sincronizar esses itens automaticamente.
        </p>
        <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="checklist-template-new">
          <Plus className="h-4 w-4 mr-1.5" /> Novo template
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border/70 max-w-2xl">
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <div className="text-sm font-medium mb-1">Nenhum template configurado</div>
            <div className="text-xs text-muted-foreground mb-4">
              Crie templates de checklist para padronizar as tarefas que precisam ser feitas em cada veículo.
            </div>
            <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-1.5" /> Criar primeiro template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 max-w-3xl">
          {items.map((t) => (
            <Card key={t.id} className="border-border/70" data-testid="checklist-template-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">{t.name}</div>
                      {!t.is_active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">INATIVO</span>
                      )}
                    </div>
                    <div className="flex gap-3 mt-2 text-[11px] text-muted-foreground">
                      <div>Ordem: <span className="tabular font-medium">{t.order}</span></div>
                      {t.default_due_days && (
                        <div>Prazo padrão: <span className="font-medium">{t.default_due_days} dias após compra</span></div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid="checklist-template-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid="checklist-template-delete">
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar template' : 'Novo template de checklist'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome do item *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Revisão de freios"
                data-testid="template-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ordem</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Prazo (dias)</Label>
                <Input
                  type="number"
                  value={form.default_due_days}
                  onChange={(e) => setForm((f) => ({ ...f, default_due_days: e.target.value }))}
                  placeholder="Ex: 7"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Dias após a compra</p>
              </div>
            </div>
            <label className="flex items-center gap-2 p-2 rounded border border-border/70 cursor-pointer hover:bg-muted/30">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
              />
              <div className="text-sm">
                <div className="font-medium">Ativo</div>
                <div className="text-[11px] text-muted-foreground">Quando ativo, será incluído automaticamente ao sincronizar</div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="template-save-button">
              {editing ? 'Salvar alterações' : 'Criar template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
